import { hasValidEmail } from "@/helpers/email";
import { isValidVin } from "@/helpers/vehicle";
import { isValidPhoneNumber } from "@/helpers/phone_number";
import { cleanupPhoneNumber } from '@/helpers/phone_number';


export function isNonEmptyString(string: string): boolean {
    const trimmed = string?.trim();
    return trimmed?.length > 0;
}

// error messages and rules for vuetify components
export const errorMessages = {
    vin: "Invalid VIN",
    email: "Invalid email address",
    phoneNumber: "Invalid phone number",
    required: "Field is required",
    url: "Please enter a valid URL (must start with http:// or https://)",
};

export function isValidUrl(value: string): boolean {
    if (!isNonEmptyString(value)) {
        return false;
    }
    try {
        const parsed = new URL(value.trim());
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return false;
        }
        if (!parsed.hostname) {
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

export const rules = {
    required: (value: any) => {
        if (typeof value === 'string') {
            return !!value && isNonEmptyString(value) || errorMessages.required;
        } else if (typeof value === 'number') {
            return !!value && value > 0 || errorMessages.required;
        } else {
            return !!value || errorMessages.required;
        }
    },

    email: (value: string) =>
        !value || hasValidEmail(value) || errorMessages.email,

    phoneNumber: (value: string, countryIsoTwo: string) =>
        !value || isValidPhoneNumber(value, countryIsoTwo) || errorMessages.phoneNumber,

    vin: (value: string) =>
        !value || isValidVin(value) || errorMessages.vin,

    positiveNumber: (value: number | null) =>
        (value !== null && value > 0) || 'Must be a positive number',

    nonNegative: (value: number | null) =>
        value === null || value >= 0 || 'Must be 0 or greater',

    nonNegativeRequired: (value: number | null) =>
        (value !== null && value >= 0) || 'Must be 0 or greater',

    url: (value: string) =>
        !value || isValidUrl(value) || errorMessages.url,
};

/**
 * Shared phone number validation used by multiple form components.
 * Returns the same shape that `phone_input_fields.vue` expects.
 */
export function validatePhoneNumber(phoneNumber: string, countryIsoTwo: string): {
    valid: boolean;
    errorMessage: string | null;
    numberType: string | null;
    normalizedPhoneNumber: string;
} {
    const phone = phoneNumber;
    if (!(phone?.length > 0)) {
        return {
            valid: false,
            errorMessage: null,
            numberType: null,
            normalizedPhoneNumber: '',
        };
    }

    const cleanPN = cleanupPhoneNumber(phone, countryIsoTwo);
    return {
        valid: cleanPN.valid,
        errorMessage: cleanPN.valid ? null : 'Please enter a valid phone number',
        numberType:
            cleanPN.valid && cleanPN.numberType
                ? cleanPN.numberType[0].toUpperCase() + cleanPN.numberType.slice(1)
                : null,
        normalizedPhoneNumber: cleanPN.valid && cleanPN.phoneNumber ? cleanPN.phoneNumber : '',
    };
}
